import React, { useState } from "react";
import { Table, Button, Input, Space, Typography, Popconfirm, App } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, SaveOutlined, CloseOutlined } from "@ant-design/icons";
import { addGroupToSchool, removeGroupFromSchool, editGroupInSchool } from "../../../../services/schools/school.service";
import { serverMessage } from "../../../../utils/serverMessage";

const { Title } = Typography;

interface GroupsTabProps {
  groups: string[];
  setGroups: React.Dispatch<React.SetStateAction<string[]>>;
  schoolId: number | null;
  onGroupsSaved?: () => void;
  onRefreshClasses?: () => Promise<void>;
}

const GroupsTab: React.FC<GroupsTabProps> = ({ groups, setGroups, schoolId, onGroupsSaved, onRefreshClasses }) => {
  // message via App.useApp(): antd v5's static API does not render under
  // React 19 without the compat patch, so every toast this tab raised — the
  // errors AND the confirmations — was silently dropped.
  const { message } = App.useApp();
  const [editingGroupIndex, setEditingGroupIndex] = useState<number | null>(null);
  const [editingGroupName, setEditingGroupName] = useState<string>("");
  const [adding, setAdding] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleAddGroup = () => {
    const newGroups = ["", ...groups];
    setGroups(newGroups);
    setEditingGroupIndex(0);
    setEditingGroupName("");
    setAdding(true);
  };

  const handleEditGroup = (index: number, groupName: string) => {
    setEditingGroupIndex(index);
    setEditingGroupName(groupName);
    setAdding(false);
  };

  const handleSaveGroup = async () => {
    if (!editingGroupName.trim()) {
      message.error("El nombre del grupo es obligatorio.");
      return;
    }

    if (!schoolId) {
      message.error("No se puede guardar: ID de escuela no encontrado.");
      return;
    }
    
    // Validar duplicados
    const trimmedName = editingGroupName.trim();
    const isDuplicate = groups.some(
      (group, index) => index !== editingGroupIndex && group.toLowerCase() === trimmedName.toLowerCase()
    );
    
    if (isDuplicate) {
      message.error("Ya existe un grupo con ese nombre.");
      return;
    }

    setLoading(true);
    try {
      if (adding) {
        // Crear nuevo grupo usando el endpoint específico
        const response = await addGroupToSchool(schoolId, trimmedName);
        if (response) {
          const updatedGroups = [...groups];
          updatedGroups[editingGroupIndex!] = trimmedName;
          setGroups(updatedGroups);
          message.success("Grupo creado correctamente.");
          onGroupsSaved?.(); // Notificar al componente padre
        } else {
          message.error("Error al crear el grupo.");
          return;
        }
      } else {
        // Para edición, usar el nuevo endpoint para actualizar en la base de datos
        const currentGroupName = groups[editingGroupIndex!];
        const response = await editGroupInSchool(schoolId, currentGroupName, trimmedName);
        if (response) {
          const updatedGroups = [...groups];
          updatedGroups[editingGroupIndex!] = trimmedName;
          setGroups(updatedGroups);
          message.success("Grupo actualizado correctamente. Las clases relacionadas también se han actualizado.");
          onGroupsSaved?.(); // Notificar al componente padre
          
          // Refrescar las clases para reflejar los cambios
          if (onRefreshClasses) {
            await onRefreshClasses();
          }
        } else {
          message.error("Error al actualizar el grupo.");
          return;
        }
      }
      
      setEditingGroupIndex(null);
      setEditingGroupName("");
      setAdding(false);
    } catch (error) {
      console.error("Error saving group:", error);
      message.error(
        serverMessage(error, "Ocurrió un error al guardar el grupo.")
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    if (adding && editingGroupIndex === 0) {
      setGroups(groups.slice(1));
    }
    setEditingGroupIndex(null);
    setEditingGroupName("");
    setAdding(false);
  };

  const handleDeleteGroup = async (index: number) => {
    if (!schoolId) {
      message.error("No se puede eliminar: ID de escuela no encontrado.");
      return;
    }

    const groupName = groups[index];
    setLoading(true);
    try {
      const response = await removeGroupFromSchool(schoolId, groupName);
      
      if (response) {
        const updatedGroups = groups.filter((_, i) => i !== index);
        setGroups(updatedGroups);
        message.success("Grupo eliminado correctamente.");
        onGroupsSaved?.(); // Notificar al componente padre
      } else {
        message.error("Error al eliminar el grupo.");
      }
    } catch (error) {
      console.error("Error deleting group:", error);
      // The API blocks the delete while classes still use the group and says
      // which ones — swallowing that left the operator with no way forward.
      message.error(
        serverMessage(error, "Ocurrió un error al eliminar el grupo.")
      );
    } finally {
      setLoading(false);
    }
  };

  const handleGroupNameChange = (value: string) => {
    setEditingGroupName(value.replace(/[^a-zA-Z0-9\s_-]/g, ""));
  };

  // Convertir array de strings a formato de tabla
  const dataSource = groups.map((group, index) => ({
    key: index,
    index,
    name: group,
  }));

  const columns = [
    {
      title: "Nombre del Grupo",
      dataIndex: "name",
      key: "name",
      render: (name: string, record: any) => {
        if (editingGroupIndex === record.index) {
          return (
            <Input
              value={editingGroupName}
              onChange={(e) => handleGroupNameChange(e.target.value)}
              placeholder="Ej: ROJO, A1, GRUPO1"
              maxLength={20}
              autoFocus
            />
          );
        }
        return name;
      },
    },
    {
      title: "Acciones",
      key: "actions",
      render: (_: any, record: any) => {
        if (editingGroupIndex === record.index) {
          return (
            <Space>
              {adding ? (
                <Button icon={<SaveOutlined />} type="primary" size="small" onClick={handleSaveGroup} loading={loading}>
                  Guardar
                </Button>
              ) : (
                <Popconfirm
                  title="¿Confirmar edición del grupo?"
                  description="Al editar este grupo, también se actualizarán las clases relacionadas."
                  onConfirm={handleSaveGroup}
                  okText="Sí, editar"
                  cancelText="Cancelar"
                >
                  <Button icon={<SaveOutlined />} type="primary" size="small" loading={loading}>
                    Guardar
                  </Button>
                </Popconfirm>
              )}
              <Button icon={<CloseOutlined />} size="small" onClick={handleCancelEdit}>
                Cancelar
              </Button>
            </Space>
          );
        }
        return (
          <Space>
            <Button icon={<EditOutlined />} size="small" onClick={() => handleEditGroup(record.index, record.name)}>
              Editar
            </Button>
            <Popconfirm
              title="¿Seguro que deseas eliminar este grupo?"
              description="Esta acción eliminará el grupo inmediatamente de la base de datos."
              onConfirm={() => handleDeleteGroup(record.index)}
              okText="Sí"
              cancelText="No"
            >
              <Button icon={<DeleteOutlined />} danger size="small" loading={loading}>
                Eliminar
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <div className="groups-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>Gestión de Grupos</Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAddGroup}
          className="add-group-btn"
        >
          Agregar Grupo
        </Button>
      </div>
      
      <Table
        columns={columns}
        dataSource={dataSource}
        rowKey="key"
        pagination={false}
        className="groups-table"
        locale={{ emptyText: "No hay grupos registrados. Haz clic en 'Agregar Grupo' para comenzar." }}
      />
    </div>
  );
};

export default GroupsTab;
